import React, { useCallback, useEffect, useState } from 'react';
import { Collapse } from '@mui/material';
import AlertBlock from 'components/shared/blocks/AlertBlock';
import { pushNotificationStatus } from 'common/constants/options';
import { arePushNotificationsSupported } from 'common/utils/notification';
import useNotifications from 'hooks/notifications/useNotifications';

const SNOOZE_KEY = 'otx_push_banner_snoozed_until';
const SNOOZE_DAYS = 7;
const AUTO_HIDE_MS = 5000;

/** ¿El usuario cerró el aviso hace menos de SNOOZE_DAYS? */
const isSnoozed = () => {
  try {
    const until = Number(window.localStorage.getItem(SNOOZE_KEY));
    return Boolean(until) && Date.now() < until;
  } catch {
    return false;
  }
};

const snooze = () => {
  try {
    window.localStorage.setItem(
      SNOOZE_KEY,
      String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000)
    );
  } catch {
    /* modo privado: el cierre vale solo para esta vista */
  }
};

/**
 * Aviso de permisos de push. Antes se renderizaba directo desde el layout a partir del
 * estado del permiso, así que la X no tenía efecto (nada guardaba el cierre) y el banner
 * seguía ahí. Ahora: se auto-oculta a los 5 s, la X lo silencia una semana, y el aviso de
 * "bloqueadas" (que el usuario no puede resolver desde la app) también es descartable.
 */
function PushPermissionBanner() {
  const { askForNotificationPermission, pushNotifications } = useNotifications();

  const isPending = pushNotifications === pushNotificationStatus.default;
  const isBlocked = pushNotifications === pushNotificationStatus.denied;
  const shouldRender = arePushNotificationsSupported && (isPending || isBlocked);

  const [visible, setVisible] = useState(() => shouldRender && !isSnoozed());

  // El permiso puede cambiar en caliente (el usuario acepta en el diálogo del navegador).
  useEffect(() => {
    setVisible(shouldRender && !isSnoozed());
  }, [shouldRender]);

  useEffect(() => {
    if (!visible) return undefined;
    const timer = window.setTimeout(() => setVisible(false), AUTO_HIDE_MS);
    return () => window.clearTimeout(timer);
  }, [visible]);

  const handleClose = useCallback(() => {
    snooze();
    setVisible(false);
  }, []);

  if (!shouldRender) return null;

  return (
    <Collapse in={visible} unmountOnExit timeout={250}>
      <AlertBlock
        actions={isPending ? { allow: askForNotificationPermission } : {}}
        type={isPending ? 'allowPushNotifications' : 'blockedPushNotifications'}
        onClose={handleClose}
      />
    </Collapse>
  );
}

export default PushPermissionBanner;
